import { Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import {
  findReadySlotForPatient,
  formatDemoCopyTag,
  useDemoCopyResetState,
  useDemoRosterQuery,
  useReseedDemoPatientsMutation,
} from '@/entities/sandbox-demo';
import { Button } from '@/shared/ui/shadcn/button';

import { ResetDemoPatientDialog } from './ResetDemoPatientDialog';

interface ResetDemoPatientControlProps {
  patientUserId: string;
}

/**
 * A seeding slot carries no `patient_user_id`, so the in-flight slug is what
 * ties the reset back to this chart until the slot is ready again.
 *
 * Only starts a reset and reflects its state. The settle/fail side effects
 * belong to `useDemoCopyResetTransitions`, mounted by the workspace page so
 * they survive this control being swapped out of the left panel.
 *
 * It also carries the copy tag, because this is the one place already proven
 * to appear on a demo chart and nowhere else: it renders only for a chart that
 * the roster reports as a ready copy of this clinician's, and the roster has
 * no slots unless `compute_sandbox_demo_availability` says available. Hanging
 * the tag here means production never sees it and no second gate exists to
 * drift out of step with this one.
 */
export function ResetDemoPatientControl({ patientUserId }: ResetDemoPatientControlProps) {
  const { data: roster } = useDemoRosterQuery();
  const reseed = useReseedDemoPatientsMutation();
  const resetState = useDemoCopyResetState(patientUserId);
  const [dialogOpen, setDialogOpen] = useState(false);

  const readySlot = findReadySlotForPatient(roster, patientUserId);
  const inFlightSlug = resetState.type === 'idle' ? undefined : resetState.slug;
  const inFlightDisplayName = resetState.type === 'idle' ? undefined : resetState.displayName;
  const slug = readySlot?.slug ?? inFlightSlug;
  const displayName = readySlot?.display_name ?? inFlightDisplayName;
  const isResetting = reseed.isPending || resetState.type === 'resetting';

  if (slug === undefined || displayName === undefined) {
    return null;
  }

  const copyTag = formatDemoCopyTag(patientUserId);

  const handleConfirm = () => {
    setDialogOpen(false);
    reseed.mutate([slug]);
  };

  return (
    <>
      <span
        className="rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground"
        title={`Demo copy ${patientUserId} of fixture ${slug}`}
      >
        {copyTag}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={isResetting}
        aria-busy={isResetting}
        onClick={() => setDialogOpen(true)}
        title="Restore this demo patient to the committed case"
      >
        {isResetting ? (
          <>
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            Resetting…
          </>
        ) : (
          <>
            <RotateCcw className="size-3.5" aria-hidden="true" />
            Reset to base
          </>
        )}
      </Button>
      <ResetDemoPatientDialog
        open={dialogOpen}
        displayName={displayName}
        copyTag={copyTag}
        isPending={reseed.isPending}
        onConfirm={handleConfirm}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}
